import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { TaskDialog } from "./TaskDialog";
import { TaskList } from "./TaskList";
import { TaskFilters } from "./TaskFilters";
import { useTranslation } from "react-i18next";
import { useProfileData } from "@/hooks/useProfileData";

interface TaskManagementProps {
  tenderId: string;
}

type TaskStatus = "todo" | "in_progress" | "in_review" | "completed" | "blocked";
type TaskPriority = "low" | "medium" | "high";

interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  due_date?: string;
  tender_id: string;
  assignee_id?: string;
  creator_id: string;
  created_at: string;
  updated_at: string;
  assignee?: {
    email?: string;
    company_name?: string;
  };
  creator?: {
    email?: string;
    company_name?: string;
  };
}

export const TaskManagement = ({ tenderId }: TaskManagementProps) => {
  const { t } = useTranslation('offerManagement');
  const { toast } = useToast();
  const { profile } = useProfileData();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log("Fetching tasks for tender:", tenderId);
  }, [tenderId]);

  const { data: tasks = [], refetch, isLoading } = useQuery<Task[]>({
    queryKey: ["tender-tasks", tenderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tender_tasks")
        .select(`
          *,
          assignee:profiles!tender_tasks_assignee_id_fkey(
            email,
            company_name
          ),
          creator:profiles!tender_tasks_creator_id_fkey(
            email,
            company_name
          )
        `)
        .eq("tender_id", tenderId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching tasks:", error);
        throw error;
      }
      
      console.log("Fetched tasks:", data);
      return data as Task[];
    },
    enabled: Boolean(tenderId),
  });

  const handleRefetch = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: ["tender-tasks", tenderId] });
      await refetch();
    } catch (error) {
      console.error("Error refetching tasks:", error);
      toast({
        title: t("errorMessages.generalError"),
        description: t("tasks.fetchError"),
        variant: "destructive",
      });
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesStatus = statusFilter === "all" || task.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || task.priority === priorityFilter;
    return matchesStatus && matchesPriority;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t('tasks.title')}</h3>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('tasks.newTask')}
        </Button>
      </div>

      <TaskFilters
        statusFilter={statusFilter}
        priorityFilter={priorityFilter}
        onStatusChange={setStatusFilter}
        onPriorityChange={setPriorityFilter}
      />

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          {t('common.loading')}
        </div>
      ) : (
        <TaskList 
          tasks={filteredTasks}
          onTasksChange={handleRefetch} 
          tenderId={tenderId} 
        />
      )}

      <TaskDialog
        tenderId={tenderId}
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSuccess={() => {
          handleRefetch();
          toast({
            title: t('tasks.taskCreated'),
            description: t('tasks.taskCreatedDesc'),
          });
        }}
      />
    </div>
  );
};
